<template>
  <div
    class="app-table"
    :class="{
      'app-table--responsive': responsive,
      'app-table--sm': small,
      'app-table--busy': busy
    }">
    <table
      class="app-table__table"
      :class="{
        'app-table__table--striped': striped,
        'app-table__table--hover': hover,
        'app-table__table--bordered': bordered,
        'app-table__table--borderless': borderless,
        'app-table__table--sm': small
      }">
      <caption v-if="caption">{{ caption }}</caption>
      <thead>
        <tr>
          <th
            v-for="field in normalizedFields"
            :key="field.key"
            :scope="'col'"
            :class="field.thClass"
            :style="field.thStyle">
            <slot :name="`head(${field.key})`" :field="field" :label="field.label">
              {{ field.label }}
            </slot>
          </th>
        </tr>
      </thead>
      <tbody v-if="busy">
        <tr>
          <td :colspan="normalizedFields.length">
            <slot name="table-busy">
              <div class="app-table__busy">
                <span class="app-table__spinner"></span>
                Cargando...
              </div>
            </slot>
          </td>
        </tr>
      </tbody>
      <tbody v-else-if="showEmpty && !items.length">
        <tr>
          <td :colspan="normalizedFields.length">
            <slot name="empty">
              <div class="app-table__empty">{{ emptyText }}</div>
            </slot>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr
          v-for="(item, index) in items"
          :key="primaryKey ? item[primaryKey] : index"
          :class="trClass(item, index)">
          <td
            v-for="field in normalizedFields"
            :key="field.key"
            :class="field.tdClass"
            :style="field.tdStyle">
            <slot
              :name="`cell(${field.key})`"
              :value="cellValue(item, field)"
              :unformatted="rawValue(item, field)"
              :item="item"
              :field="field"
              :index="index">
              {{ cellValue(item, field) }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
/**
 * AppTable
 *
 * Tabla genérica con API compatible con bootstrap-vue <b-table>
 * (props items/fields/busy/responsive/striped/small/hover + slots
 * #cell(<key>) / #head(<key>) / #empty / #table-busy), para facilitar
 * una futura migración a b-table sin cambiar los consumidores.
 */
export default {
  name: 'AppTable',
  inheritAttrs: false,
  props: {
    items: {
      type: Array,
      default: () => []
    },
    fields: {
      type: Array,
      default: () => []
    },
    primaryKey: {
      type: String,
      default: ''
    },
    busy: {
      type: Boolean,
      default: false
    },
    responsive: {
      type: Boolean,
      default: false
    },
    striped: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    hover: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: false
    },
    borderless: {
      type: Boolean,
      default: false
    },
    showEmpty: {
      type: Boolean,
      default: false
    },
    emptyText: {
      type: String,
      default: 'No hay datos disponibles'
    },
    caption: {
      type: String,
      default: ''
    },
    tbodyTrClass: {
      type: [Function, String, Array],
      default: null
    }
  },
  computed: {
    normalizedFields () {
      return (this.fields || []).map(f => {
        if (typeof f === 'string') {
          return { key: f, label: f }
        }
        return { ...f }
      })
    }
  },
  methods: {
    rawValue (item, field) {
      if (item == null) return undefined
      if (!field.key || field.key.indexOf('.') === -1) {
        return item[field.key]
      }
      return field.key.split('.').reduce((acc, part) => {
        if (acc == null) return undefined
        return acc[part]
      }, item)
    },
    cellValue (item, field) {
      const value = this.rawValue(item, field)
      if (typeof field.formatter === 'function') {
        return field.formatter(value, field.key, item)
      }
      return value
    },
    trClass (item, index) {
      if (typeof this.tbodyTrClass === 'function') {
        return this.tbodyTrClass(item, 'row', index)
      }
      return this.tbodyTrClass
    }
  }
}
</script>

<style scoped>
.app-table {
  width: 100%;
}

.app-table--responsive {
  overflow-x: auto;
}

.app-table__table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
}

.app-table__table th,
.app-table__table td {
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid var(--app-border-color, #e5e7eb);
  text-align: left;
  font-size: 0.875rem;
  vertical-align: middle;
}

.app-table__table th {
  font-weight: 600;
  background: #f9fafb;
  color: #374151;
}

.app-table__table--sm th,
.app-table__table--sm td {
  padding: 0.4rem 0.5rem;
  font-size: 0.8125rem;
}

.app-table__table--striped tbody tr:nth-of-type(odd) {
  background: rgba(0, 0, 0, 0.02);
}

.app-table__table--hover tbody tr:hover {
  background: rgba(0, 0, 0, 0.04);
}

.app-table__table--bordered th,
.app-table__table--bordered td {
  border: 1px solid var(--app-border-color, #e5e7eb);
}

.app-table__table--borderless th,
.app-table__table--borderless td {
  border-bottom: none;
}

.app-table__busy,
.app-table__empty {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.app-table__busy {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.app-table__spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: var(--app-primary, #0f2835);
  border-radius: 50%;
  animation: app-table-spin 0.6s linear infinite;
}

@keyframes app-table-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
